import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Space, Typography } from 'antd';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';

import PageHeader from '../../uiKitComponents/pageHeader';
import Form from '../../uiKitComponents/form';
import DatePicker from '../../uiKitComponents/datePicker';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import Table from '../../uiKitComponents/table';
import PaginationSummary from '../../components/PaginationSummary';

import {
  getScoreQualityReport,
  cleanReports,
  setCleanOnUnmountTrue as setReportsCleanOnUnmountTrue,
} from '../../store/actions/reportPagesAction';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const getMomentDate = (date) => (date ? moment(date, 'YYYY-MM-DD') : null);

const disableMoreThan6MonthsAndFuture = (current) => {
  const today = moment();
  const sixMonthsAgo = today.subtract(6, 'months');
  return (
    current &&
    (current.isBefore(sixMonthsAgo, 'day') || current.isAfter(today, 'day'))
  );
};

const initialState = {
  page: 1,
  dates: { from: null, to: null },
};

const columns = [
  {
    dataIndex: 'date',
    title: 'Date',
    render: (date) => moment(date).format('DD/MM/YYYY'),
  },
  {
    dataIndex: 'average_score',
    title: 'Average Score',
  },
];

const histogramOptions = {
  responsive: true,
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: { ticks: { font: { size: 9 }, autoSkip: false } },
    y: { ticks: { stepSize: 1, font: { size: 9 } } },
  },
};

const mapDataToHistogram = (data) =>
  data?.reduce(
    (acc, item) => {
      acc.labels.push(item.bucket);
      acc.dataset.push(item.count);
      return acc;
    },
    { labels: [], dataset: [] },
  );
const Histogram = ({ title, data }) => {
  const histogramData = useMemo(() => {
    const mappedData = mapDataToHistogram(data);

    return {
      labels: mappedData.labels,
      datasets: [
        {
          data: mappedData.dataset,
          backgroundColor: '#88173f',
        },
      ],
    };
  }, [data]);

  return (
    <div style={{ flex: 1 }}>
      <Title level={4}>{title}</Title>
      <Bar data={histogramData} options={histogramOptions} />
    </div>
  );
};

Histogram.defaultProps = {
  title: '',
  data: [],
};

Histogram.propTypes = {
  title: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      average_score: PropTypes.number,
    }),
  ),
};

const ScoreQualityPage = () => {
  const [form] = Form.useForm();

  const [state, setState] = useSessionStorage('score-quality', {
    ...initialState,
  });
  const { page, dates } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const { cleanOnUnmount, scoreQuality } = useSelector((store) => ({
    cleanOnUnmount: store.reportPages.cleanOnUnmount,
    scoreQuality: store.reportPages.payload.scoreQuality,
  }));

  const dispatch = useDispatch();

  const fetchData = useCallback(() => {
    setLoading(true);

    getScoreQualityReport({
      from: dates?.from?.string,
      to: dates?.to?.string,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('score-quality');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, dates]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  useEffect(() => {
    if (scoreQuality?.selected_range) {
      const { from, to } = scoreQuality.selected_range;
      form.setFieldsValue({ range: [getMomentDate(from), getMomentDate(to)] });
    }
  }, [scoreQuality?.selected_range, form]);

  useMount(() => {
    setReportsCleanOnUnmountTrue()(dispatch);
  });

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('score-quality');
      cleanReports()(dispatch);
    }
  });

  const onSubmit = ({ range }) => {
    setState((prevState) => ({
      ...prevState,
      dates:
        range && range.length
          ? {
              from: {
                string: moment(range[0].$d).format('DD-MM-YYYY'),
                locale: range[0],
              },
              to: {
                string: moment(range[1].$d).format('DD-MM-YYYY'),
                locale: range[1],
              },
            }
          : initialState.dates,
    }));
  };

  const onReset = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      dates: initialState.dates,
    }));
  };

  const onTableChange = useCallback(
    (pagination, _, __, { action }) => {
      const newState = {};
      if (action === 'paginate') {
        newState.page = pagination.current;
      }
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  const pagination = useMemo(() => {
    const per_page = 25;
    const total = scoreQuality?.score_drift?.length || 0;

    return {
      page,
      per_page,
      total,
      from: (page - 1) * per_page + 1,
      to: Math.min(page * per_page, total),
    };
  }, [scoreQuality, page]);

  if (error)
    return (
      <PageHeader className="page-main-header" title="Score Quality">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader className="page-main-header" title="Score Quality">
        <Card className="header-card">
          <h3>Select Dates</h3>
          <Form
            form={form}
            onFinish={onSubmit}
            onReset={onReset}
            className="d-flex align-items-center justify-content-between w-100"
          >
            <Form.Item name="range">
              <RangePicker
                format="DD/MM/YYYY"
                disabledDate={disableMoreThan6MonthsAndFuture}
              />
            </Form.Item>
            <Space>
              <Form.Item>
                <Button type="dashed" htmlType="reset">
                  Clear
                </Button>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Space>
          </Form>
        </Card>
      </PageHeader>
      <div className="summary-container">
        {Object.entries(scoreQuality?.ratios || {}).map(([key, value]) => (
          <button type="button" key={key} className={'summary-el'}>
            <span className="text-capitalize">
              {key.split('_').join(' ')}:{' '}
            </span>
            <span>{value}</span>
          </button>
        ))}
        {Object.entries(scoreQuality?.reopen_rollback || {}).map(
          ([key, value]) => (
            <button type="button" key={key} className={'summary-el'}>
              <span className="text-capitalize">
                {key.split('_').join(' ')}:{' '}
              </span>
              <span>{value}</span>
            </button>
          ),
        )}
      </div>
      <div className="page-main-content d-flex" style={{ gap: 32 }}>
        <Histogram title="Selected Range" data={scoreQuality?.histogram} />
        <Histogram title="All Time" data={scoreQuality?.current_histogram} />
      </div>
      <div className="page-main-content">
        <PaginationSummary meta={pagination} />
        <Table
          columns={columns}
          rowKey="country"
          dataSource={scoreQuality?.score_drift}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: pagination?.per_page,
            total: pagination?.total,
            current: pagination?.page,
          }}
        />
        <PaginationSummary meta={pagination} />
      </div>
    </div>
  );
};

export default ScoreQualityPage;
